import { accountNeedsSetup, isSetupPath } from './accountSetup';
import { consumePendingContactPath } from './openContactReturn';
import { groupContinuePath } from './groupContinue';
import { introContinuePath } from './introContinue';
import { safeNext } from './phoneAppsPrompt';
import { readResumePath } from './resumePath';

/**
 * Scan context that must survive web sign-in (S1-d / I-61).
 *
 * Save to PeopleiKnow on `/s/{token}` stashes the token + how-we-met draft,
 * then sends the visitor to `/login`. After password / MFA / magic-link /
 * email-verify / OAuth, land back on `/s/{token}` — not Home — and finish
 * the add. MFA repair still goes to `/settings/2fa`.
 */

export const PENDING_SHARE_TOKEN = 'pending_share_token';
export const PENDING_SHARE_HWM = 'pending_share_hwm';
export const PENDING_SHARE_INTENT = 'pending_share_intent';
export const PENDING_SHARED_CONTACT = 'pending_shared_contact';

const TOKEN_RE = /^[A-Za-z0-9_-]{8,64}$/;
const SHARED_TOKEN_RE = /^[A-Za-z0-9_-]{8,128}$/;

function store() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function shareContinuePath() {
  const s = store();
  if (!s) return null;
  const token = s.getItem(PENDING_SHARE_TOKEN);
  if (!token || !TOKEN_RE.test(token)) return null;
  return `/s/${token}`;
}

export function stashPendingSharedContact(token) {
  const s = store();
  if (!s || !token || !SHARED_TOKEN_RE.test(token)) return;
  s.setItem(PENDING_SHARED_CONTACT, token);
}

export function peekPendingSharedContact() {
  const s = store();
  if (!s) return null;
  const token = s.getItem(PENDING_SHARED_CONTACT);
  if (!token || !SHARED_TOKEN_RE.test(token)) return null;
  return token;
}

export function sharedContactContinuePath() {
  const token = peekPendingSharedContact();
  return token ? `/shared/${token}` : null;
}

export function consumePendingSharedContact(expected) {
  const token = peekPendingSharedContact();
  if (!token) return null;
  if (expected && token !== expected) return null;
  const s = store();
  if (s) s.removeItem(PENDING_SHARED_CONTACT);
  return token;
}

/** Scan, group session, introduction, or shared card still waiting on this browser. */
export function inviteContinuePath() {
  const s = store();
  if (!s) return null;
  const token = s.getItem('pending_invite_token');
  if (!token || !TOKEN_RE.test(token)) return null;
  return `/invite/${token}`;
}

export function accountContinuePath() {
  return shareContinuePath() || groupContinuePath() || introContinuePath() || inviteContinuePath() || sharedContactContinuePath();
}

function guestReturnPath() {
  return shareContinuePath() || groupContinuePath() || introContinuePath() || inviteContinuePath() || sharedContactContinuePath();
}

export function postSignInPath(user, { mfaRepair = false, next = '' } = {}) {
  if (mfaRepair) return '/settings/2fa';
  const guest = guestReturnPath();
  const requested = safeNext(next || '') || readResumePath();
  const serverResume = safeNext(user?.onboarding_resume_path || '');
  const needsSetup = accountNeedsSetup(user);
  const keep = (path) => (path && (needsSetup || !isSetupPath(path)) ? path : '');
  const dest = needsSetup
    ? (guest || keep(requested) || keep(serverResume) || '/onboarding')
    : (consumePendingContactPath() || guest || keep(requested) || keep(serverResume) || '/');
  if (user?.phone_apps_prompt_pending) {
    const safe = safeNext(dest) || (needsSetup ? '/onboarding' : '/');
    return `/welcome/apps?next=${encodeURIComponent(safe)}`;
  }
  return dest;
}

export function stashPendingShare({ token, hwm, intent = 'add' }) {
  const s = store();
  if (!s || !token) return;
  s.setItem(PENDING_SHARE_TOKEN, token);
  if (intent) s.setItem(PENDING_SHARE_INTENT, intent);
  if (hwm) {
    try {
      s.setItem(PENDING_SHARE_HWM, JSON.stringify(hwm));
    } catch {
      /* quota / private mode */
    }
  }
}

export function peekPendingShare() {
  const s = store();
  if (!s) return null;
  const token = s.getItem(PENDING_SHARE_TOKEN);
  if (!token) return null;
  let hwm = null;
  try {
    hwm = JSON.parse(s.getItem(PENDING_SHARE_HWM) || 'null');
  } catch {
    hwm = null;
  }
  return { token, intent: s.getItem(PENDING_SHARE_INTENT) || '', hwm };
}

export function consumePendingShare(expectedToken) {
  const pending = peekPendingShare();
  if (!pending) return null;
  if (expectedToken && pending.token !== expectedToken) return null;
  const s = store();
  if (s) {
    s.removeItem(PENDING_SHARE_TOKEN);
    s.removeItem(PENDING_SHARE_HWM);
    s.removeItem(PENDING_SHARE_INTENT);
  }
  return pending;
}
